import { Box, Button, Stack, Typography, IconButton, Tooltip, Tab } from "@compass/core";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Skeleton from "@mui/material/Skeleton";
import Tabs from "@mui/material/Tabs";
import { useState } from "react";
import { DashboardCard } from "../components/DashboardCard";
import RefreshIcon from '@mui/icons-material/Refresh';
import { NotificationDataProvider, useNotificationData } from "../contexts/NotificationDataContext";

const NotificationsCardContent = () => {
  const {
    newNotifications,
    dismissedNotifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    fetchNewNotifications,
    fetchDismissedNotifications,
    refetchAll,
  } = useNotificationData();

  const [expandedId, setExpandedId] = useState<number | false>(false);
  const [selectedTab, setSelectedTab] = useState<"new" | "dismissed">("new");
  const [newPage, setNewPage] = useState(1);
  const [dismissedPage, setDismissedPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 5;

  const handleTabChange = async (_event: React.SyntheticEvent, newValue: "new" | "dismissed") => {
    setSelectedTab(newValue);
    if (newValue === "dismissed") {
      setDismissedPage(1);
      await fetchDismissedNotifications(1, pageSize);
    } else {
      setNewPage(1);
      await fetchNewNotifications(1, pageSize);
    }
  };

  const handleAccordionChange = (notificationId: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedId(isExpanded ? notificationId : false);
      if (isExpanded && selectedTab === "new") {
        markAsRead(notificationId);
      }
  };

  const formatDate = (date: string | Date): string => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleNextPage = async () => {
    if (selectedTab === "new") {
      const nextPage = newPage + 1;
      setNewPage(nextPage);
      await fetchNewNotifications(nextPage, pageSize);
    } else {
      const nextPage = dismissedPage + 1;
      setDismissedPage(nextPage);
      await fetchDismissedNotifications(nextPage, pageSize);
    }
  };

  const handlePreviousPage = async () => {
    if (selectedTab === "new") {
      const prevPage = Math.max(1, newPage - 1);
      setNewPage(prevPage);
      await fetchNewNotifications(prevPage, pageSize);
    } else {
      const prevPage = Math.max(1, dismissedPage - 1);
      setDismissedPage(prevPage);
      await fetchDismissedNotifications(prevPage, pageSize);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      setNewPage(1);
      setDismissedPage(1);
      await refetchAll();
    } finally {
      setIsRefreshing(false);
    }
  };

  const currentNotifications = selectedTab === "new" ? newNotifications : dismissedNotifications;
  const currentPage = selectedTab === "new" ? newPage : dismissedPage;
  const hasNextPage = currentNotifications.length === pageSize;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="New" value="new" />
          <Tab label="Past Notifications" value="dismissed" />
        </Tabs>
        <Tooltip title="Refresh Notifications">
          <IconButton 
            size="small" 
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            sx={{ 
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Stack direction="column" spacing="sm" sx={{ flexGrow: 1 }}>
          <Skeleton variant="rounded" height={60} />
          <Skeleton variant="rounded" height={60} />
          <Skeleton variant="rounded" height={60} />
          <Skeleton variant="rounded" height={40} />
        </Stack>
      ) : error ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      ) : currentNotifications.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 4,
            gap: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {selectedTab === "new"
              ? "No new notifications"
              : "No past notifications"}
          </Typography>
        </Box>
      ) : (
        <>
          <Stack direction="column" spacing="sm" sx={{ flexGrow: 1, minHeight: 0 }}>
            {currentNotifications.map((notification) => (
              <Accordion
                key={notification.id}
                expanded={expandedId === notification.id}
                onChange={handleAccordionChange(notification.id)}
                sx={{
                  "&:before": { display: "none" },
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:not(:last-child)": { borderBottom: 0 },
                  "&.Mui-expanded": { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 48,
                    "&.Mui-expanded": { minHeight: 48 },
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0",
                      "&.Mui-expanded": { margin: "12px 0" },
                    },
                  }}
                >
                  <Stack direction="row" spacing="sm" alignItems="center">
                    {selectedTab === "new" && !notification.isRead && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      fontWeight={notification.isRead ? "normal" : "medium"}
                    >
                      {formatDate(notification.createdDate)} - {notification.title}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary">
                    {notification.content}
                  </Typography>
                   {notification.isBug && (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          bgcolor: "error.light",
                          color: "error.contrastText",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          width: "fit-content",
                        }}
                      >
                        <Typography variant="caption" fontWeight="medium">
                          Bug Fix
                        </Typography>
                      </Box>
                    )}
    
                    {/* Display User Manual link */}
                    {notification.userManualLink && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          href={notification.userManualLink}
                          rel="noopener noreferrer"
                        >
                          View User Manual
                        </Button>
                      </Box>
                    )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          {currentNotifications.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, pt: 2 }}>
              <Button 
                size="small"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={handlePreviousPage}
              >
                Previous
              </Button>
              <Typography variant="body2">
                Page {currentPage}
              </Typography>
              <Button 
                size="small"
                variant="outlined"
                disabled={!hasNextPage}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </Box>
          )}

          {selectedTab === "new" && newNotifications.length > 0 && (
            <Box sx={{ pt: 2 }}>
              <Button variant="outlined" fullWidth onClick={markAllAsRead}>
                Mark All as Read
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export const NotificationsCard = () => {
  return (
    <NotificationDataProvider>
      <DashboardCard title="Notifications">
        <NotificationsCardContent />
      </DashboardCard>
    </NotificationDataProvider>
  );
};